/* global globalThis */
/**
 * Sizes a sidebar surface to exactly the gap between the fixed mobile header and the bottom nav
 * bar on mobile, so its content isn't cut off on iOS. Shared by WCSideBarV2 and
 * WCNestedSidebarPanel.
 */
export default {
  data() {
    return {
      mobileSidebarTop: 0,
      mobileSidebarHeight: 0,
    };
  },
  computed: {
    mobileSidebarStyle() {
      if (this.platform.isMobile) {
        return {
          top: `${this.mobileSidebarTop}px`,
          height: `${this.mobileSidebarHeight}px`,
        };
      }
      return {
        top: 'var(--mobile-header-height, 0px)',
        height: 'calc(100vh - var(--mobile-header-height, 0px) - var(--mobile-nav-height, 0px))',
      };
    },
  },
  mounted() {
    this.updateMobileSidebarMetrics();

    globalThis.addEventListener('resize', this.updateMobileSidebarMetrics);
    globalThis.addEventListener('orientationchange', this.updateMobileSidebarMetrics);

    if (typeof ResizeObserver === 'function') {
      this.metricsResizeObserver = new ResizeObserver(this.updateMobileSidebarMetrics);
      const { header, nav } = this.getSidebarBoundaryElements();
      if (header) this.metricsResizeObserver.observe(header);
      if (nav) this.metricsResizeObserver.observe(nav);
    }
  },
  beforeUnmount() {
    globalThis.removeEventListener('resize', this.updateMobileSidebarMetrics);
    globalThis.removeEventListener('orientationchange', this.updateMobileSidebarMetrics);
    if (this.metricsResizeObserver) {
      this.metricsResizeObserver.disconnect();
    }
  },
  methods: {
    // `.mobile-header` (WCMobileHeaderV2.vue) and `.wc-mobile-nav-v2` (WCBottomNavigation.vue)
    // are the fixed header/bottom-nav bars the sidebar sizes itself between. Each is a single
    // persistent element in the DOM (not re-created), but their height/position can change
    // (resize, orientation change, safe-area inset), so we re-query them rather than caching the
    // elements, and re-measure via the resize listeners/ResizeObserver above.
    getSidebarBoundaryElements() {
      return {
        header: document.querySelector('.mobile-header'),
        nav: document.querySelector('.wc-mobile-nav-v2'),
      };
    },
    /**
     * Measures the real bottom edge of the mobile header and the real top edge of the bottom
     * nav bar, then sizes the sidebar to exactly the gap between them. This avoids relying on the
     * `--mobile-header-height`/`--mobile-nav-height` CSS vars staying in sync across every
     * layout/header combination, which had been leaving the menu's bottom content cut off or
     * the bottom nav hidden behind it on iOS.
     */
    updateMobileSidebarMetrics() {
      if (!this.platform.isMobile) return;

      const { header, nav } = this.getSidebarBoundaryElements();
      // Round outward (top down, bottom up) so subpixel rounding can only cause a sliver of
      // overlap with the header/nav, never a visible gap between them and the sidebar.
      const headerBottom = header ? Math.floor(header.getBoundingClientRect().bottom) : 0;
      const navTop = nav ? Math.ceil(nav.getBoundingClientRect().top) : globalThis.innerHeight;

      this.mobileSidebarTop = Math.max(headerBottom, 0);
      this.mobileSidebarHeight = Math.max(navTop - headerBottom, 0);
    },
  },
};
